import React, { useEffect, useState } from 'react';
import { getImportDef, runImport } from '../../api/imports';
import type { RunOutcome } from '../../api/imports';
import type {
  ExistingMode,
  ImportDef,
  ImportDefSummary,
} from '../../types/Import';
import { t } from '../../i18n/t';
import { showErrorToast } from '../../components/ErrorToast';
import { LopButton } from '../../components/LopButton';
import { LopFilePicker } from '../../components/LopFilePicker';
import { useAsyncAction } from '../../hooks/useAsyncAction';

interface ImportRunPanelProps {
  defs: ImportDefSummary[];
  loadingDefs: boolean;
}

export function ImportRunPanel({ defs, loadingDefs }: ImportRunPanelProps) {
  const activeDefs = defs.filter((d) => d.isActive);

  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [selectedDef, setSelectedDef] = useState<ImportDef | null>(null);
  const [mode, setMode] = useState<ExistingMode>('Skip');
  const [nullLiteralAsEmpty, setNullLiteralAsEmpty] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [outcome, setOutcome] = useState<RunOutcome | null>(null);

  // Load the selected program's config to show its target table and to seed
  // the existing-mode select from its default.
  useEffect(() => {
    if (selectedId === '') {
      setSelectedDef(null);
      return;
    }
    let cancelled = false;
    getImportDef(selectedId)
      .then((def) => {
        if (cancelled) return;
        setSelectedDef(def);
        setMode(def.config.defaultExistingMode);
      })
      .catch(() => {
        if (cancelled) return;
        setSelectedDef(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const targetLabel = (() => {
    if (!selectedDef) return '';
    const summary = defs.find((d) => d.importDefId === selectedDef.importDefId);
    return summary?.targetTable ?? selectedDef.config.targetTable;
  })();

  const canRun = selectedId !== '' && file !== null;

  const { run: handleRun, running } = useAsyncAction(async () => {
    if (selectedId === '' || !file) return;
    setOutcome(null);
    try {
      const result = await runImport(selectedId, mode, file, nullLiteralAsEmpty);
      setOutcome(result);
    } catch (e: unknown) {
      showErrorToast(e, { title: t('errorToast.titleOperation') });
    }
  }, { paintFirst: true });

  return (
    <section className="gimport-section">
      <h2 className="gimport-section__title">{t('imports.run.title')}</h2>

      <div className="gimport-field">
        <label className="gimport-field__label" htmlFor="gimport-run-program">
          {t('imports.run.program')}
        </label>
        <select
          id="gimport-run-program"
          className="gimport-select"
          value={selectedId}
          disabled={loadingDefs}
          onChange={(e) =>
            setSelectedId(e.target.value === '' ? '' : Number(e.target.value))
          }
        >
          <option value="" disabled>
            {t('imports.run.selectProgram')}
          </option>
          {activeDefs.map((d) => (
            <option key={d.importDefId} value={d.importDefId}>
              {d.importName}
            </option>
          ))}
        </select>
        {!loadingDefs && activeDefs.length === 0 && (
          <span className="gimport-muted">{t('imports.run.noProgramsHint')}</span>
        )}
      </div>

      {selectedDef && (
        <div className="gimport-readonly">
          {t('imports.run.targetTable')}: <strong>{targetLabel}</strong>
        </div>
      )}

      <div className="gimport-field">
        <label className="gimport-field__label" htmlFor="gimport-run-mode">
          {t('imports.run.mode')}
        </label>
        <select
          id="gimport-run-mode"
          className="gimport-select"
          value={mode}
          onChange={(e) => setMode(e.target.value as ExistingMode)}
        >
          <option value="Skip">{t('imports.run.modeSkip')}</option>
          <option value="Update">{t('imports.run.modeUpdate')}</option>
          <option
            value="UpdateOnly"
            title={t('imports.run.modeUpdateOnlyHint')}
          >
            {t('imports.run.modeUpdateOnly')}
          </option>
        </select>
      </div>

      <div className="gimport-field">
        <label className="gimport-checkbox">
          <input
            type="checkbox"
            checked={nullLiteralAsEmpty}
            onChange={(e) => setNullLiteralAsEmpty(e.target.checked)}
          />
          <span>{t('imports.run.nullLiteralAsEmpty')}</span>
        </label>
      </div>

      <div className="gimport-field">
        <label className="gimport-field__label" htmlFor="gimport-run-file">
          {t('imports.run.file')}
        </label>
        <LopFilePicker
          id="gimport-run-file"
          accept=".csv,.xlsx,.zip"
          files={file ? [file] : []}
          onChange={(files) => setFile(files[0] ?? null)}
          hint={t('imports.run.fileHint')}
        />
      </div>

      <div className="gimport-actions">
        <LopButton
          variant="primary"
          disabled={!canRun}
          loading={running}
          onClick={() => handleRun()}
        >
          {t('imports.run.runButton')}
        </LopButton>
      </div>

      {outcome && outcome.ok && (
        <div className="gimport-result gimport-result--success" role="status">
          <p className="gimport-result__title">{t('imports.run.success')}</p>
          <p className="gimport-result__line">
            {t('imports.run.summary', {
              total: outcome.result.totalRows,
              inserted: outcome.result.inserted,
              updated: outcome.result.updated,
              skipped: outcome.result.skipped,
            })}
          </p>
        </div>
      )}

      {outcome && !outcome.ok && (
        <div className="gimport-result gimport-result--error" role="alert">
          <p className="gimport-result__title">{outcome.error}</p>
          {outcome.issues.length > 0 && (
            <table className="gimport-table gimport-issues-table">
              <thead>
                <tr>
                  <th>{t('imports.run.issueRow')}</th>
                  <th>{t('imports.run.issueColumn')}</th>
                  <th>{t('imports.run.issueValue')}</th>
                  <th>{t('imports.run.issueReason')}</th>
                </tr>
              </thead>
              <tbody>
                {outcome.issues.map((issue, i) => (
                  <tr key={i}>
                    <td>
                      {issue.row === 0 ? t('imports.run.empty') : issue.row}
                    </td>
                    <td>{issue.column}</td>
                    <td>{issue.value ?? t('imports.run.empty')}</td>
                    <td>{issue.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
